export type FieldErrors = Record<string, string[]>;

const safeKey = (key: string) =>
  key !== '__proto__' && key !== 'constructor' && key !== 'prototype';

function messages(value: unknown): string[] {
  if (typeof value === 'string' && value.trim()) return [value];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

export function normalizeValidationDetails(value: unknown): FieldErrors | undefined {
  const result: FieldErrors = Object.create(null);
  const add = (key: string, value: unknown) => {
    if (!safeKey(key)) return;
    const next = messages(value);
    if (next.length) result[key] = [...(result[key] ?? []), ...next];
  };

  if (Array.isArray(value)) {
    for (const issue of value) {
      if (!issue || typeof issue !== 'object') continue;
      const record = issue as Record<string, unknown>;
      const path = Array.isArray(record.path) ? record.path.map(String).join('.') : '';
      if (path) add(path, record.message);
      else add('_errors', record.message);
    }
  } else if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const fields =
      record.fieldErrors && typeof record.fieldErrors === 'object'
        ? (record.fieldErrors as Record<string, unknown>)
        : record;
    for (const [key, detail] of Object.entries(fields)) {
      if (key !== 'fieldErrors' && key !== 'formErrors') add(key, detail);
    }
    add('_errors', record.formErrors);
  }

  return Object.keys(result).length ? result : undefined;
}

const camelToSnake = (value: string) =>
  value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export function getFieldError(details: FieldErrors | undefined, field: string): string | undefined {
  if (!details) return undefined;
  const snake = camelToSnake(field);
  return details[field]?.[0] ?? details[snake]?.[0];
}

export function firstInvalidField(details: FieldErrors | undefined): string | undefined {
  return details ? Object.keys(details).find((key) => key !== '_errors') : undefined;
}
